export function withResolvers<T>(): PromiseWithResolvers<T> {
  if (typeof Promise.withResolvers === 'function') {
    return Promise.withResolvers();
  }

  let resolve!: (value: T | PromiseLike<T>) => void;
  let reject!: (reason?: any) => void;

  const promise = new Promise<T>((_resolve, _reject) => {
    resolve = _resolve;
    reject = _reject;
  });

  return {
    resolve,
    reject,
    promise,
  };
}

export function wait(ms: number): Promise<void>;
export function wait<T>(ms: number, value: T): Promise<T>;
export function wait<T = void>(ms: number, resolvedValue?: T) {
  return new Promise<T | undefined>((resolve) => setTimeout(() => resolve(resolvedValue), ms));
}

/**
 * Convert a callback that is obtained asynchronously into a callback that can be called
 * synchronously.
 *
 * If the callback is called immediately then when the promise resolves it calls the callback
 * with the same arguments.
 *
 * @param promise Promise that resolves to a callback.
 */
export function toSyncCallback<Args extends any[]>(promise: Promise<(...args: Args) => void>): (...args: Args) => void {
  let calledWith: Args | undefined;
  let callback: (...args: Args) => void | undefined;

  promise.then((_callback) => {
    callback = _callback;

    if (calledWith) {
      callback.call(undefined, ...calledWith);
      calledWith = undefined;
    }
  });

  return (...args) => {
    if (callback) {
      callback.call(undefined, ...args);
    } else {
      calledWith = args;
    }
  };
}

export interface PromiseWithQueue<T> {
  promise: Promise<T[]>;
  enqueue(...promises: Promise<T>[]): void;
}

export function promiseQueue<T>(): PromiseWithQueue<T> {
  const { promise, resolve, reject } = withResolvers<T[]>();

  let pendingCount = 0;
  let pending = true;

  const result: Awaited<T>[] = [];

  return {
    promise,
    enqueue(...promises) {
      if (!pending) {
        throw new Error('Queue resolved');
      }

      pendingCount += promises.length;

      Promise.all(promises).then(
        (values) => {
          if (!pending) {
            return;
          }

          result.push(...values);

          pendingCount -= values.length;

          if (pendingCount === 0) {
            pending = false;
            resolve(result);
          }
        },
        (error) => {
          pending = false;
          reject(error);
        },
      );
    },
  };
}
